import React, { useState } from 'react';
import { X, Music, Image, FileText, Link as LinkIcon, Send, User } from 'lucide-react';
import { useUser } from '../contexts/UserContext';
import SongPicker from './SongPicker';
import AttachmentDialog from './AttachmentDialog';

interface CreatePostDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (content: string, attachments: Array<{
    type: 'link' | 'image' | 'file';
    url: string;
    title?: string;
    description?: string;
    thumbnailUrl?: string;
  }>) => void;
}

function CreatePostDialog({ isOpen, onClose, onSubmit }: CreatePostDialogProps) {
  const { user } = useUser();
  const [content, setContent] = useState('');
  const [showSongPicker, setShowSongPicker] = useState(false);
  const [showAttachmentDialog, setShowAttachmentDialog] = useState<'link' | 'image' | 'file' | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [attachments, setAttachments] = useState<Array<{
    type: 'link' | 'image' | 'file';
    url: string;
    title?: string;
    description?: string;
    thumbnailUrl?: string;
  }>>([]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim() || submitting) return;

    setSubmitting(true);
    try {
      await onSubmit(content, attachments);
      setContent('');
      setAttachments([]);
      onClose();
    } catch (error) {
      console.error('Error creating post:', error);
      alert('Failed to create post');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAttachment = (attachment: {
    type: 'link' | 'image' | 'file';
    url: string;
    title?: string;
    description?: string;
    thumbnailUrl?: string;
  }) => {
    setAttachments([...attachments, attachment]);
    setShowAttachmentDialog(null);
  };

  const removeAttachment = (index: number) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="p-4 border-b flex justify-between items-center flex-shrink-0">
          <h3 className="text-lg font-semibold text-center flex-1">Create post</h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="p-4 flex-1 overflow-y-auto">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-gray-100 overflow-hidden">
                {user?.avatarUrl ? (
                  <img
                    src={user.avatarUrl}
                    alt={user.username}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <User className="h-6 w-6 text-gray-400" />
                  </div>
                )}
              </div>
              <div>
                <p className="font-medium">{user?.displayName || user?.username}</p>
              </div>
            </div>

            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="What's on your musical mind?"
              className="w-full px-4 py-2 border-0 focus:outline-none resize-none bg-white text-lg"
              rows={4}
              autoFocus
            />

            {attachments.length > 0 && (
              <div className="mt-4 space-y-2">
                {attachments.map((attachment, index) => (
                  <div key={index} className="relative">
                    {attachment.type === 'image' ? (
                      <div className="relative rounded-lg overflow-hidden">
                        <img
                          src={attachment.url}
                          alt={attachment.title || 'Attached image'}
                          className="w-full h-auto max-h-48 object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => removeAttachment(index)}
                          className="absolute top-2 right-2 p-1 bg-black bg-opacity-50 rounded-full text-white hover:bg-opacity-75 transition-opacity"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                        <span className="truncate">{attachment.title || attachment.url}</span>
                        <button
                          type="button"
                          onClick={() => removeAttachment(index)}
                          className="p-1 text-gray-500 hover:text-gray-700"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="p-4 border-t flex-shrink-0">
            <div className="border rounded-lg p-3 mb-4">
              <p className="text-sm font-medium text-gray-600 mb-2">Add to your post</p>
              <div className="flex space-x-2">
                <button
                  type="button"
                  onClick={() => setShowSongPicker(true)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-indigo-600"
                >
                  <Music className="h-6 w-6" />
                </button>
                <button
                  type="button"
                  onClick={() => setShowAttachmentDialog('image')}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-green-600"
                >
                  <Image className="h-6 w-6" />
                </button>
                <button
                  type="button"
                  onClick={() => setShowAttachmentDialog('link')}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-blue-600"
                >
                  <LinkIcon className="h-6 w-6" />
                </button>
                <button
                  type="button"
                  onClick={() => setShowAttachmentDialog('file')}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-orange-600"
                >
                  <FileText className="h-6 w-6" />
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={!content.trim() || submitting}
              className={`w-full py-2 px-4 rounded-lg flex items-center justify-center gap-2 ${
                !content.trim() || submitting
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-indigo-600 text-white hover:bg-indigo-700'
              } transition-colors`}
            >
              <Send className="h-5 w-5" />
              {submitting ? 'Posting...' : 'Post'}
            </button>
          </div>
        </form>

        {showSongPicker && (
          <SongPicker
            isOpen={showSongPicker}
            onClose={() => setShowSongPicker(false)}
            onSelect={(song) => {
              setContent((prev) => 
                `${prev}\n\nListening to ${song.title} by ${song.artist}`
              );
              setShowSongPicker(false);
            }}
          />
        )}

        <AttachmentDialog
          isOpen={!!showAttachmentDialog}
          onClose={() => setShowAttachmentDialog(null)}
          onAttach={handleAttachment}
          type={showAttachmentDialog || 'link'}
        />
      </div>
    </div>
  );
}

export default CreatePostDialog;